import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AccountDTO } from '../../../../../shared/account-dto';
import { AccountService } from '../../account.service';
import { CANADA_STATE_OPTIONS, US_STATES_OPTIONS } from '../../constants';
import { CountryService } from '../../country.service';
import { CustomerService } from '../../customer.service';
import { CustomerAddressComponent } from '../../profile/customer-address/customer-address.component';

@Component({
  selector: 'pv-customer-address-form',
  templateUrl: './customer-address-form.component.html',
  styleUrls: ['./customer-address-form.component.scss']
})
export class CustomerAddressFormComponent implements OnInit {
  @Input() defaultText: any;
  @Input() defaultAddress: any;
  @Input() default: boolean;
  @Input() collapsed: boolean;
  @Input() hideUpdate: boolean;
  @Input() selectable: boolean;
  @Input() editable: boolean;
  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  editing = false;
  adding = false;
  showForm = false;
  customer: any = {}
  addressForm: FormGroup;
  usStates = US_STATES_OPTIONS
  canadaStates = CANADA_STATE_OPTIONS
  countries: any[];
  account: AccountDTO;

  @ViewChild('customerAddress') customerAddress: CustomerAddressComponent

  constructor(
    private customerService: CustomerService,
    private countryService: CountryService,
    private accountService: AccountService,
  ) { }

  ngOnInit() {
    this.account = this.accountService.account;
    this.accountService.accountSubject.subscribe(account => this.account = account)

    // Subscribe to customer
    this.customer = this.customerService.checkoutDTO.customer
    this.customerService.checkoutSubject.subscribe(checkout => this.customer = checkout.customer)

    this.initializeForm()

    // Get country list
    this.countryService.getCountries()
    .subscribe(countries => {
      this.countries = countries.sort(a => a.iso == 'US' ? -1 : 1)
      this.addressForm.get('ship_country_iso').patchValue('US')
    })
  }

  addAddress() {
    if (this.adding) {
      const data = this.addressForm.value;
      this.customerService.addAddress({
        id: data.addressId,
        firstname: data.ship_firstname,
        lastname: data.ship_lastname,
        addr1: data.ship_addr1,
        addr2: data.ship_addr2,
        company: data.company,
        city: data.ship_city,
        state: data.ship_state,
        phone: data.ship_phone,
        zip: data.ship_zip,
        country_iso: data.ship_country_iso,
        tax_id: data.tax_id,
      })
      .subscribe(address => {
        this.showForm = false;
        this.editing = false;
        this.adding = false;
        this.resetAddress()
        this.customerAddress.getAddresses()
      })
    }
  }

  editAddress() {
    if (this.editing) {
      const data = this.addressForm.value
      if (data.addressId) {
        this.customerService.updateAddress({
          id: data.addressId,
          firstname: data.ship_firstname,
          lastname: data.ship_lastname,
          addr1: data.ship_addr1,
          addr2: data.ship_addr2,
          company: data.company,
          city: data.ship_city,
          state: data.ship_state,
          phone: data.ship_phone,
          zip: data.ship_zip,
          country_iso: data.ship_country_iso,
          tax_id: data.tax_id,
        })
        .subscribe(address => {
          this.showForm = false;
          this.resetAddress()
          this.customerAddress.getAddresses()
        })
      } else {
        this.updateAddress(data)

        this.onChange.emit(data)
      }
    }
  }

  initializeForm() {
    this.showForm = !(this.customer.id > 0)
    this.addressForm = new FormGroup({
      'id': new FormControl(this.customer.id, []),
      'addressId': new FormControl(this.defaultAddress && this.defaultAddress.id, []),
      'is_wholesale': new FormControl(this.customer.is_wholesale || false, [Validators.required]),
      'ship_firstname': new FormControl(this.defaultAddress && this.defaultAddress.ship_firstname, [Validators.required]),
      'ship_lastname': new FormControl(this.defaultAddress && this.defaultAddress.ship_lastname, [Validators.required]),
      'company': new FormControl(this.defaultAddress && this.defaultAddress.company, []),
      'ship_addr1': new FormControl(this.defaultAddress && this.defaultAddress.ship_addr1, [Validators.required]),
      'ship_addr2': new FormControl(this.defaultAddress && this.defaultAddress.ship_addr2, []),
      'ship_city': new FormControl(this.defaultAddress && this.defaultAddress.ship_city, [Validators.required]),
      'ship_state': new FormControl(this.defaultAddress && this.defaultAddress.ship_state, []),
      'ship_zip': new FormControl(this.defaultAddress && this.defaultAddress.ship_zip, [Validators.required]),
      'ship_country_iso': new FormControl(this.defaultAddress && this.defaultAddress.ship_country_iso, [Validators.required]),
      'tax_id': new FormControl(this.defaultAddress && this.defaultAddress.tax_id, []),
      'ship_phone': new FormControl(this.defaultAddress && this.defaultAddress.ship_phone, []),
    }, { validators: [this.phoneValidator, this.stateValidator, (x) => this.taxIdValidator(x), this.companyValidator] })

    this.addressForm.valueChanges.subscribe(value => {
      this.onChange.emit((this.addressForm.valid && !this.editing) ? value : null)
    })
  }

  resetAddress() {
    this.editing = false;
    this.addressForm.get('addressId').setValue(null)
    this.addressForm.get('ship_firstname').setValue('')
    this.addressForm.get('ship_lastname').setValue('')
    this.addressForm.get('ship_addr1').setValue('')
    this.addressForm.get('ship_addr2').setValue('')
    this.addressForm.get('company').setValue('')
    this.addressForm.get('ship_city').setValue('')
    this.addressForm.get('ship_state').setValue('')
    this.addressForm.get('ship_zip').setValue('')
    this.addressForm.get('ship_country_iso').setValue('US')
    this.addressForm.get('tax_id').setValue('')
    this.addressForm.get('ship_phone').setValue('')
    this.addressForm.markAsUntouched()
  }

  updateAddress(address) {
    if (address.id) {
      this.addressForm.get('addressId').setValue(address.id)
    }
    this.addressForm.get('ship_firstname').setValue(address.firstname)
    this.addressForm.get('ship_lastname').setValue(address.lastname)
    this.addressForm.get('ship_addr1').setValue(address.addr1)
    this.addressForm.get('ship_addr2').setValue(address.addr2)
    this.addressForm.get('company').setValue(address.company)
    this.addressForm.get('ship_city').setValue(address.city)
    this.addressForm.get('ship_state').setValue(address.state)
    this.addressForm.get('ship_zip').setValue(address.zip)
    this.addressForm.get('ship_country_iso').setValue(address.country_iso)
    this.addressForm.get('tax_id').setValue(address.tax_id)
    this.addressForm.get('ship_phone').setValue(address.phone)
  }

  onSelectAddress(address) {
    this.showForm = false;
    this.editing = false;
    this.adding = false;
    (!address || address.id == this.addressForm.get('addressId').value)
      ? this.resetAddress()
      : this.updateAddress(address)
  }

  onEditAddress(address) {
    if (address && address.id) {
      this.adding = false;
      this.editing = true;
      this.showForm = true;
      this.updateAddress(address)
    } else {
      this.showForm = false;
      this.editing = false;
      this.resetAddress()
    }
  }

  onNewAddress() {
    this.editing = false;
    this.adding = true;
    this.showForm = true;
    this.resetAddress()
  }

  stateValidator(control: FormGroup): ValidationErrors | null {
    const state = control.get('ship_state')
    const country = control.get('ship_country_iso')
    if (country.value == 'US') {
      return !!state.value ? null : { 'state': true }
    }
    return;
  }

  phoneValidator(control: FormGroup): ValidationErrors | null {
    const phone = control.get('ship_phone')
    const country = control.get('ship_country_iso')
    if (!country || !country.value || country.value == 'US') {
      return;
    } else {
      return !!phone.value ? null : { 'international' : true }
    }
  }

  taxIdValidator(control): ValidationErrors | null {
    if (this.accountService.account && this.accountService.account.id == 2) {
      const country = control.get('ship_country_iso').value
      const taxId = control.get('tax_id').value
      if (country == 'BR') {
        return !!taxId ? null : { 'tax_id': true }
      } else {
        return;
      }
    } else {
      return;
    }

  }

  sameAsBilling() {
    this.updateAddress(this.defaultAddress)
  }

  // #654 Company Validator method
  companyValidator(control: FormGroup): ValidationErrors | null {

    //console.log("companyValidator");
    const is_wholesale = control.get('is_wholesale').value;
    const company = control.get('company').value;
    //console.log('is_wholesale: ' + !!is_wholesale);
    //console.log('company: ' + company);

    if (!!is_wholesale && company)
    {
      if (company.trim().length < 1)
      {
        //console.log('bad company 1');
        return { 'company': true }
      }
    }
    else if (!!is_wholesale)
    {
      //console.log('bad company 2');
      return { 'company': true }
    }
    else  // D2C doesn't need company
    {
      //console.log('good company');
      return null;
    }
  }

}
